import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import EyeOutlineIcon from 'mdi-react/EyeOutlineIcon';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import PackageVariantClosedIcon from 'mdi-react/PackageVariantClosedIcon';
import PlayCircleOutlineIcon from 'mdi-react/PlayCircleOutlineIcon';
import PauseCircleOutlineIcon from 'mdi-react/PauseCircleOutlineIcon';
import { useGridApiRef } from '@mui/x-data-grid';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import OmmnioDataGrid from 'components/common/OmmnioDataGrid';
import OtaPlatformIcon from 'components/@admin/@ota/OtaPlatformIcon';
import {
  useDisableUpdateMutation,
  useEnableUpdateMutation,
  useListChannelsQuery,
  useListUpdatesQuery,
} from 'store/app/entities/OtaSlice';
import { I18n } from 'utils/i18n';
import renderDateTimeCell from 'utils/renderDateTimeCell';

const PAGE_SIZE = 50;

const autosizeOptions = {
  includeOutliers: false,
  includeHeaders: true,
  expand: true,
};

const platformFilters = [
  { key: 'all', value: '' },
  { key: 'ios', value: 'ios' },
  { key: 'android', value: 'android' },
];

const enabledFilters = [
  { key: 'all', value: '' },
  { key: 'enabled', value: 'true' },
  { key: 'disabled', value: 'false' },
];

const shortId = id => (id ? String(id).slice(-8) : '');

const OtaUpdatesPage = () => {
  const apiRef = useGridApiRef();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: chData } = useListChannelsQuery();
  const channels = useMemo(() => chData?.channels || [], [chData]);
  const defaultChannel = channels[0]?.name || '';

  const channelName = searchParams.get('channel') || defaultChannel;
  const platform = searchParams.get('platform') || '';
  const branch = searchParams.get('branch') || '';
  const enabled = searchParams.get('enabled') || '';
  const q = searchParams.get('q') || '';
  const runtimeVersion = searchParams.get('runtimeVersion') || '';
  const [localQ, setLocalQ] = useState(q);
  const [listEpoch, setListEpoch] = useState(0);

  const filterKey = useMemo(
    () => [channelName, platform, branch, enabled, q, runtimeVersion, listEpoch].join('|'),
    [channelName, platform, branch, enabled, q, runtimeVersion, listEpoch],
  );

  const [cursorIso, setCursorIso] = useState(null);
  const [mergedUpdates, setMergedUpdates] = useState([]);
  const [appliedFilterKey, setAppliedFilterKey] = useState(filterKey);

  useEffect(() => {
    setLocalQ(q);
  }, [q]);

  const resetList = () => {
    setCursorIso(null);
    setMergedUpdates([]);
    setAppliedFilterKey('');
  };

  const { data, error, isFetching } = useListUpdatesQuery(
    {
      channelName,
      limit: PAGE_SIZE,
      platform: platform || undefined,
      branch: branch || undefined,
      enabled: enabled || undefined,
      q: q || undefined,
      runtimeVersion: runtimeVersion || undefined,
      createdAtBefore: cursorIso || undefined,
      listEpoch,
    },
    { skip: !channelName },
  );

  useEffect(() => {
    if (!data?.updates) return;
    if (!cursorIso && isFetching) return;
    const batch = data.updates;
    setMergedUpdates(prev => {
      if (!cursorIso) return batch;
      const seen = new Set(prev.map(u => u._id));
      return [...prev, ...batch.filter(u => !seen.has(u._id))];
    });
    setAppliedFilterKey(filterKey);
  }, [data, cursorIso, isFetching, filterKey]);

  // Hide rows until they belong to the active filters (prevents stale-data flash).
  const updates = useMemo(
    () => (appliedFilterKey === filterKey ? mergedUpdates : []),
    [appliedFilterKey, filterKey, mergedUpdates],
  );

  // autosizeOnMount runs before async rows arrive; resize after each batch is applied.
  useEffect(() => {
    if (!updates.length) return undefined;
    const timer = window.setTimeout(() => {
      apiRef.current?.autosizeColumns(autosizeOptions)?.catch(() => {});
    }, 0);
    return () => window.clearTimeout(timer);
  }, [updates, apiRef]);

  const setParam = (key, value) => {
    resetList();
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const handleSearchChange = e => {
    const v = e.target.value;
    setLocalQ(v);
    setParam('q', v.trim());
  };

  const bumpListAfterMutation = () => {
    resetList();
    setListEpoch(e => e + 1);
  };

  const [disableUpdate] = useDisableUpdateMutation();
  const [enableUpdate] = useEnableUpdateMutation();

  const lastBatchLen = data?.updates?.length ?? 0;
  const canLoadMore = updates.length > 0 && lastBatchLen === PAGE_SIZE;

  const onLoadMore = () => {
    const last = updates[updates.length - 1];
    if (!last?.createdAt) return;
    setCursorIso(new Date(last.createdAt).toISOString());
  };

  const columns = [
    {
      minWidth: 200,
      field: 'revision',
      editable: false,
      sortable: false,
      headerName: I18n.t('AdminOtaScene.Updates.revision'),
      renderCell: params => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              bgcolor: 'primary.ultraLight',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '0 0 auto',
            }}
          >
            <SvgIcon sx={{ fontSize: 18 }}>
              <PackageVariantClosedIcon />
            </SvgIcon>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>
              {params.row.revision != null ? `rev.${params.row.revision}` : shortId(params.row._id)}
            </Typography>
            <Typography
              variant="caption"
              noWrap
              sx={{
                color: 'text.secondary',
                display: 'block',
                fontFamily: 'monospace',
              }}
            >
              {params.row.branch} · {params.row.runtimeVersion}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'platform',
      minWidth: 72,
      align: 'center',
      headerAlign: 'center',
      editable: false,
      sortable: false,
      headerName: I18n.t('AdminOtaScene.Updates.colPlatform'),
      renderCell: params => <OtaPlatformIcon platform={params.value} />,
    },
    {
      field: 'createdAt',
      minWidth: 170,
      editable: false,
      sortable: false,
      headerName: I18n.t('AdminOtaScene.Updates.colCreated'),
      renderCell: renderDateTimeCell,
    },
    {
      field: 'enabled',
      minWidth: 120,
      editable: false,
      sortable: false,
      headerName: I18n.t('AdminOtaScene.Updates.colEnabled'),
      renderCell: params => (
        <Chip
          size="small"
          variant="soft"
          color={params.value ? 'success' : 'default'}
          label={I18n.t(
            params.value
              ? 'AdminOtaScene.Updates.statusEnabled'
              : 'AdminOtaScene.Updates.statusDisabled',
          )}
          sx={{ alignSelf: 'flex-start' }}
        />
      ),
    },
    {
      field: 'actions',
      minWidth: 110,
      editable: false,
      sortable: false,
      headerName: '',
      align: 'right',
      headerAlign: 'right',
      renderCell: params => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              if (params.row.enabled) {
                disableUpdate(params.row._id).then(() => bumpListAfterMutation());
              } else {
                enableUpdate(params.row._id).then(() => bumpListAfterMutation());
              }
            }}
            title={I18n.t(
              params.row.enabled ? 'AdminOtaScene.Updates.disable' : 'AdminOtaScene.Updates.enable',
            )}
          >
            <SvgIcon fontSize="small">
              {params.row.enabled ? <PauseCircleOutlineIcon /> : <PlayCircleOutlineIcon />}
            </SvgIcon>
          </IconButton>
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              navigate(`/home/admin/ota/updates/${params.row._id}`);
            }}
            title={I18n.t('AdminOtaScene.Updates.details')}
          >
            <SvgIcon fontSize="small">
              <EyeOutlineIcon />
            </SvgIcon>
          </IconButton>
        </Box>
      ),
    },
  ];

  const GridToolbarNone = () => null;

  return (
    <Box
      sx={{
        p: 2,
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography variant="h3">{I18n.t('AdminOtaScene.Updates.title')}</Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mt: 0.5,
          }}
        >
          {I18n.t('AdminOtaScene.Updates.description')}
        </Typography>
      </Box>

      {/* Filter bar: one clean row */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          mb: 2,
        }}
      >
        {/* Channel */}
        <FormControl size="small" sx={{ minWidth: 200, mr: 0.5 }}>
          <InputLabel id="ota-ch">{I18n.t('AdminOtaScene.Updates.channel')}</InputLabel>
          <Select
            labelId="ota-ch"
            label={I18n.t('AdminOtaScene.Updates.channel')}
            value={channelName}
            onChange={e => setParam('channel', e.target.value)}
          >
            {channels.map(o => (
              <MenuItem key={o.name} value={o.name}>
                {o.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Platform chips */}
        {platformFilters.map(f => (
          <Chip
            key={f.key}
            clickable
            variant={platform === f.value ? 'filled' : 'outlined'}
            color={platform === f.value ? 'primary' : 'default'}
            label={I18n.t(`AdminOtaScene.Updates.platformFilters.${f.key}`)}
            onClick={() => setParam('platform', f.value)}
            size="small"
            sx={{ borderRadius: 999, fontWeight: 600 }}
          />
        ))}

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Status chips */}
        {enabledFilters.map(f => (
          <Chip
            key={f.key}
            clickable
            variant={enabled === f.value ? 'filled' : 'outlined'}
            color={enabled === f.value ? 'primary' : 'default'}
            label={I18n.t(`AdminOtaScene.Updates.enabledFilters.${f.key}`)}
            onClick={() => setParam('enabled', f.value)}
            size="small"
            sx={{ borderRadius: 999, fontWeight: 600 }}
          />
        ))}

        {/* Search — pushed to the right */}
        <TextField
          size="small"
          value={localQ}
          onChange={handleSearchChange}
          placeholder={I18n.t('AdminOtaScene.Updates.search')}
          sx={{ ml: 'auto', minWidth: 240 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SvgIcon sx={{ fontSize: 18, color: 'text.secondary' }}>
                    <MagnifyIcon />
                  </SvgIcon>
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 1 }}>
          {I18n.t('AdminOtaScene.loadError')}:{' '}
          {error?.data?.error || error?.message || String(error)}
        </Typography>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 320,
          height: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <OmmnioDataGrid
          apiRef={apiRef}
          loading={isFetching && updates.length === 0}
          sx={{ height: '100%', width: '100%' }}
          columns={columns}
          autosizeOptions={autosizeOptions}
          rows={updates}
          getRowId={r => r._id}
          disableEvenOdd
          resetPaginationKey={filterKey}
          pageSize={100}
          slots={{ toolbar: GridToolbarNone }}
          onRowClick={params => navigate(`/home/admin/ota/updates/${params.row._id}`)}
        />
      </Box>

      {canLoadMore && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button variant="outlined" disabled={isFetching} onClick={onLoadMore}>
            {I18n.t('AdminOtaScene.Updates.loadMore')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default OtaUpdatesPage;
